import { forwardRef, useEffect, useImperativeHandle, useRef } from "react";

// Cloudflare Turnstile (mode managed) tanpa dependensi npm: script dimuat sekali, widget di-render eksplisit.
const SCRIPT_SRC = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
const LOAD_TIMEOUT_MS = 15000;
let scriptPromise = null;

function loadScript() {
  if (window.turnstile) return Promise.resolve(window.turnstile);
  if (scriptPromise) return scriptPromise;
  scriptPromise = new Promise((resolve, reject) => {
    const s = document.createElement("script");
    s.src = SCRIPT_SRC;
    s.async = true;
    const timer = setTimeout(() => reject(new Error("timeout")), LOAD_TIMEOUT_MS);
    s.onload = () => { clearTimeout(timer); window.turnstile ? resolve(window.turnstile) : reject(new Error("no api")); };
    s.onerror = () => { clearTimeout(timer); reject(new Error("load")); };
    document.head.appendChild(s);
  }).catch((e) => { scriptPromise = null; throw e; }); // izinkan dicoba lagi setelah muat ulang komponen
  return scriptPromise;
}

/**
 * Props: siteKey, onToken(token|""), onError(). Ref: reset() — minta token baru (setelah submit/kedaluwarsa).
 */
const Turnstile = forwardRef(function Turnstile({ siteKey, onToken, onError }, ref) {
  const boxRef = useRef(null);
  const widgetId = useRef(null);
  const cb = useRef({ onToken, onError });
  cb.current = { onToken, onError };

  useImperativeHandle(ref, () => ({
    reset() {
      cb.current.onToken("");
      if (widgetId.current != null && window.turnstile) window.turnstile.reset(widgetId.current);
    },
  }));

  useEffect(() => {
    if (!siteKey) { cb.current.onError(); return undefined; }
    let cancelled = false;
    loadScript()
      .then((ts) => {
        if (cancelled || !boxRef.current) return;
        // flexible = selebar kolom (min. 300px); kolom lebih sempit (HP < 360px) -> compact agar tidak keluar kartu
        const size = boxRef.current.clientWidth >= 300 ? "flexible" : "compact";
        widgetId.current = ts.render(boxRef.current, {
          sitekey: siteKey,
          size,
          appearance: "always",
          language: "id",
          callback: (token) => cb.current.onToken(token),
          "expired-callback": () => {
            cb.current.onToken("");
            if (widgetId.current != null) ts.reset(widgetId.current);
          },
          "timeout-callback": () => {
            cb.current.onToken("");
            if (widgetId.current != null) ts.reset(widgetId.current);
          },
          "error-callback": () => {
            cb.current.onToken("");
            cb.current.onError();
            return true; // sudah ditangani, jangan lempar ke console
          },
        });
      })
      .catch(() => { if (!cancelled) cb.current.onError(); });
    return () => {
      cancelled = true;
      if (widgetId.current != null && window.turnstile) {
        try { window.turnstile.remove(widgetId.current); } catch { /* sudah terlepas */ }
      }
      widgetId.current = null;
    };
  }, [siteKey]);

  return <div ref={boxRef} className="w-full max-w-full min-w-0 overflow-hidden min-h-[65px]" data-testid="siport-turnstile" />;
});

export default Turnstile;
